import { useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { User } from '../types';

interface UseRealtimeSubscriptionsProps {
    currentUser: User | null;
    onTicketChange: () => void;
    onNotify: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

/**
 * Custom hook for managing Supabase Realtime subscriptions
 * Automatically subscribes/unsubscribes based on user authentication
 */
export const useRealtimeSubscriptions = ({
    currentUser,
    onTicketChange,
    onNotify
}: UseRealtimeSubscriptionsProps) => {
    const channelRef = useRef<RealtimeChannel | null>(null);

    useEffect(() => {
        if (!currentUser) {
            // Cleanup if user logs out
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
            return;
        }

        // Subscribe to tickets changes
        const channel = supabase
            .channel('tickets-changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'tickets'
                },
                (payload) => {
                    console.log('Realtime change:', payload);

                    // Reload tickets on any change
                    onTicketChange();

                    // Notify based on event type
                    if (payload.eventType === 'INSERT') {
                        onNotify('info', '🔔 Nuevo ticket creado');
                    } else if (payload.eventType === 'UPDATE') {
                        onNotify('info', '🔄 Ticket actualizado');
                    }
                }
            )
            .subscribe();

        channelRef.current = channel;

        // Cleanup on unmount
        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [currentUser, onTicketChange, onNotify]);

    return {
        isConnected: channelRef.current !== null
    };
};
